import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  MenuBook as MenuBookIcon,
  Send as SendIcon
} from '@mui/icons-material';
import api from '../../api';
import {
  exportFinancialsCsv,
  formatCents,
  getAccountingMapping,
  getAccountingPreview,
  getQuickBooksAccounts,
  getQuickBooksSyncs,
  saveAccountingMapping,
  sendToQuickBooks
} from './api';
import QuickBooksConnection from './QuickBooksConnection';

const ALL = '';

const GROUPS = [
  { id: 'money', title: 'Bank' },
  { id: 'income', title: 'Income' },
  { id: 'liability', title: 'Held for tenants' },
  { id: 'expense', title: 'Expenses' }
];

function pad(n) {
  return String(n).padStart(2, '0');
}

/** YYYY-MM of the month before `today`. */
export function lastMonth(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** YYYY-MM of `today`. */
export function currentMonth(today = new Date()) {
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
}

const norm = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Same rule as the server: full name (`Parent:Child`), or a plain name only one account has. */
export function findAccount(name, accounts) {
  const key = norm(name);
  if (!key) return null;
  const full = accounts.find((a) => norm(a.fullName) === key);
  if (full) return full;
  const plain = accounts.filter((a) => norm(a.name) === key);
  return plain.length === 1 ? plain[0] : null;
}

export function accountExists(name, accounts) {
  return !norm(name) || Boolean(findAccount(name, accounts));
}

/** Accounts grouped by type for the picker; shows the whole list while the box holds a chosen account. */
function filterAccountOptions(options, state) {
  const typed = norm(state.inputValue);
  if (!typed || options.some((o) => norm(o.fullName) === typed)) return options;
  return options.filter((o) => norm(o.fullName).includes(typed));
}

function formatDate(isoString) {
  const d = new Date(isoString);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Inclusive YYYY-MM-DD bounds of a YYYY-MM month. */
export function monthRange(month) {
  const [y, m] = String(month).split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${pad(lastDay)}` };
}

function monthLabel(month) {
  const [y, m] = String(month).split('-').map(Number);
  if (!y || !m) return '';
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function errorMessage(err, fallback) {
  if (err?.response?.status === 404) return 'Financial Center is not turned on for this account yet.';
  return err?.response?.data?.error || fallback;
}

function sameAccounts(a, b) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if ((a[k] || '').trim() !== (b[k] || '').trim()) return false;
  }
  return true;
}

/**
 * ZRF-FIN-QB — what goes to QuickBooks for a month, which QuickBooks account each kind of money
 * goes to, and (once connected) sending that month as one journal entry.
 * @param {{
 *   onBack?: () => void,
 *   quickbooksLink?: { action: string|null, result: string|null } | null,
 *   onQuickbooksLinkUsed?: () => void
 * }} props
 */
function AccountingPreview({ onBack, quickbooksLink = null, onQuickbooksLinkUsed }) {
  const [month, setMonth] = useState(lastMonth());
  const [propertyId, setPropertyId] = useState(ALL);
  const [properties, setProperties] = useState([]);

  const [categories, setCategories] = useState([]);
  const [saved, setSaved] = useState({});
  const [draft, setDraft] = useState({});
  const [mappingError, setMappingError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const [qbStatus, setQbStatus] = useState(null);
  const [qbAccounts, setQbAccounts] = useState([]);
  const [qbAccountsError, setQbAccountsError] = useState(null);
  const [syncs, setSyncs] = useState([]);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const connected = Boolean(qbStatus?.connected);

  useEffect(() => {
    if (!connected) {
      setQbAccounts([]);
      setSyncs([]);
      return undefined;
    }
    let cancelled = false;
    setQbAccountsError(null);
    getQuickBooksAccounts()
      .then((accounts) => {
        if (!cancelled) setQbAccounts(accounts);
      })
      .catch((err) => {
        if (!cancelled) setQbAccountsError(errorMessage(err, 'Could not load your QuickBooks accounts.'));
      });
    getQuickBooksSyncs()
      .then((rows) => {
        if (!cancelled) setSyncs(rows);
      })
      .catch(() => {
        /* History is a convenience; the server still refuses a second send. */
      });
    return () => {
      cancelled = true;
    };
  }, [connected]);

  useEffect(() => {
    let cancelled = false;
    getAccountingMapping()
      .then((data) => {
        if (cancelled) return;
        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setSaved(data?.accounts || {});
        setDraft(data?.accounts || {});
      })
      .catch((err) => {
        if (!cancelled) setMappingError(errorMessage(err, 'Could not load your QuickBooks accounts.'));
      });
    api
      .get('/api/properties')
      .then((res) => {
        if (cancelled || !Array.isArray(res?.data)) return;
        setProperties(
          res.data
            .filter((p) => p && p.id)
            .map((p) => ({ id: p.id, name: p.name || p.address || 'Property' }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {
        /* The property filter is optional; the preview still covers all properties. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const range = useMemo(() => (month ? monthRange(month) : null), [month]);

  const loadPreview = useCallback(async () => {
    if (!range) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await getAccountingPreview({ ...range, propertyId: propertyId || undefined });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setPreviewError(errorMessage(err, 'Could not build the preview.'));
    } finally {
      setPreviewLoading(false);
    }
  }, [range, propertyId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview, reloadKey]);

  const dirty = !sameAccounts(draft, saved);
  const usedThisMonth = new Set((preview?.categories || []).map((c) => c.id));

  const pickFromQuickBooks = connected && qbAccounts.length > 0;

  /** Not connected: suggested names in empty rows. Connected: only accounts the company has, also replacing names it doesn't. */
  const fillSuggestions = () => {
    setSaveResult(null);
    setDraft((prev) => {
      const next = { ...prev };
      for (const c of categories) {
        if (c.id === 'expense_tax') continue;
        const current = (next[c.id] || '').trim();
        if (!pickFromQuickBooks) {
          if (!current) next[c.id] = c.suggestedAccount;
          continue;
        }
        if (current && findAccount(current, qbAccounts)) continue;
        const match = findAccount(c.suggestedAccount, qbAccounts);
        if (match) next[c.id] = match.fullName;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const data = await saveAccountingMapping(draft);
      setSaved(data?.accounts || {});
      setDraft(data?.accounts || {});
      setSaveResult({ severity: 'success', message: 'QuickBooks accounts saved.' });
      setReloadKey((k) => k + 1);
    } catch (err) {
      setSaveResult({ severity: 'error', message: errorMessage(err, 'Could not save. Please try again.') });
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await exportFinancialsCsv({ type: 'accounting_journal', ...range, propertyId: propertyId || undefined });
    } catch (err) {
      setDownloadError('Could not download the file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const currency = preview?.currency || 'CAD';
  const accountOptions = useMemo(
    () =>
      [...qbAccounts].sort(
        (a, b) => (a.type || 'Other').localeCompare(b.type || 'Other') || a.fullName.localeCompare(b.fullName)
      ),
    [qbAccounts]
  );
  const unknownAccount = (name) => pickFromQuickBooks && !accountExists(name, qbAccounts);
  const unknownInJournal = pickFromQuickBooks && (preview?.journal || []).some((l) => l.accountName && unknownAccount(l.accountName));
  const sync = syncs.find(
    (s) => s.month === month && (s.propertyId || ALL) === propertyId && s.currency === currency
  );
  const sent = sync?.status === 'sent';
  const monthOver = Boolean(month) && month < currentMonth();
  const propertyName = properties.find((p) => p.id === propertyId)?.name;
  const scopeLabel = propertyId ? propertyName || 'this property' : 'all properties';

  let sendBlocker = null;
  if (!monthOver) sendBlocker = `You can send ${monthLabel(month)} once the month is over.`;
  else if (dirty) sendBlocker = 'Save your account choices first.';
  else if (unknownInJournal) sendBlocker = 'Pick each account from your QuickBooks list first.';
  else if (preview && !preview.readyToSend) sendBlocker = 'Choose the missing accounts above first.';

  const send = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const result = await sendToQuickBooks({ ...range, propertyId: propertyId || undefined });
      setSyncs((prev) => [result, ...prev.filter((s) => s.id !== result.id)]);
      setSendResult({
        severity: 'success',
        message: `Sent to ${qbStatus?.companyName || 'QuickBooks'} as journal entry ${result.docNumber}.`
      });
    } catch (err) {
      const rejected = err?.response?.data;
      if (rejected?.sync) setSyncs((prev) => [rejected.sync, ...prev.filter((s) => s.id !== rejected.sync.id)]);
      setSendResult({ severity: 'error', message: errorMessage(err, 'Could not send to QuickBooks. Please try again.') });
    } finally {
      setSending(false);
      setConfirmSend(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 960 }}>
      {onBack && (
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
          Financial Center
        </Button>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <MenuBookIcon color="primary" fontSize="large" />
        <Typography variant="h5" component="h1">
          QuickBooks
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        What your books send to QuickBooks for a month: rent and charges collected, deposits, and expenses.
      </Typography>
      <QuickBooksConnection link={quickbooksLink} onLinkHandled={onQuickbooksLinkUsed} onStatus={setQbStatus} />
      {qbStatus && !connected && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {qbStatus.configured
            ? 'Nothing is sent until you connect QuickBooks. Until then, check the numbers here or download the file for your accountant.'
            : 'QuickBooks isn’t connected yet, so nothing is sent. Use this to check the numbers, or download the file for your accountant.'}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ sm: 'flex-start' }}
        useFlexGap
        flexWrap="wrap"
      >
        <TextField
          type="month"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 180 }}
        />
        <TextField
          select
          label="Property"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value={ALL}>All properties</MenuItem>
          {properties.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={download}
          disabled={downloading || !preview || preview.journal.length === 0}
          sx={{ alignSelf: { sm: 'center' }, ml: { sm: 'auto' } }}
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </Button>
      </Stack>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {previewLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Building the preview" />
        </Box>
      )}

      {previewError && !previewLoading && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadPreview}>
              Try again
            </Button>
          }
        >
          {previewError}
        </Alert>
      )}

      {preview && !previewLoading && (
        <Box component="section" aria-labelledby="journal-heading" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography id="journal-heading" variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
              {`Journal entry for ${monthLabel(month)}`}
            </Typography>
            {preview.journal.length > 0 && (
              <Chip
                size="small"
                color={preview.readyToSend ? 'success' : 'warning'}
                variant="outlined"
                label={preview.readyToSend ? 'Ready for QuickBooks' : 'Needs a few choices'}
              />
            )}
          </Box>
          {preview.warnings.map((w) => (
            <Alert key={w.code + w.message} severity={w.code === 'no_activity' ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
              {w.message}
            </Alert>
          ))}
          {preview.journal.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                QuickBooks records money as debits and credits; the two totals always match. Money coming in is a
                debit to your bank account, money going out is a credit.
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
                <Table
                  size="small"
                  aria-labelledby="journal-heading"
                  sx={{ '& .MuiTableCell-root': { px: { xs: 1.25, sm: 2 } } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>QuickBooks account</TableCell>
                      <TableCell>What it covers</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.journal.map((line) => (
                      <TableRow key={line.categoryIds.join('|')} data-testid={`journal-${line.categoryIds.join('-')}`}>
                        <TableCell>
                          {line.accountName || (
                            <Typography component="span" variant="body2" color="warning.main" sx={{ fontStyle: 'italic' }}>
                              Not chosen yet
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{line.label}</TableCell>
                        <TableCell align="right">{line.debitCents ? formatCents(line.debitCents, currency) : ''}</TableCell>
                        <TableCell align="right">{line.creditCents ? formatCents(line.creditCents, currency) : ''}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 } }}>
                      <TableCell colSpan={2}>Total</TableCell>
                      <TableCell align="right" data-testid="journal-debit-total">
                        {formatCents(preview.debitTotalCents, currency)}
                      </TableCell>
                      <TableCell align="right" data-testid="journal-credit-total">
                        {formatCents(preview.creditTotalCents, currency)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
          {connected && preview.journal.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {sent ? (
                <>
                  <Alert severity="success" sx={{ mb: 1 }}>
                    {`Sent to ${sync.companyName || 'QuickBooks'} on ${formatDate(sync.sentAt)} as journal entry ${sync.docNumber}.`}
                  </Alert>
                  {sync.sourceHash !== preview.sourceHash && (
                    <Alert severity="warning">
                      Your records for this month changed after it was sent. Make the change in QuickBooks directly, or ask
                      your accountant to adjust that entry.
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  {sync?.status === 'failed' && sync.error && !sendResult && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {`The last try didn’t go through: ${sync.error}`}
                    </Alert>
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={() => setConfirmSend(true)}
                      disabled={Boolean(sendBlocker) || sending}
                    >
                      {sending ? 'Sending…' : 'Send to QuickBooks'}
                    </Button>
                    {sendBlocker && (
                      <Typography variant="body2" color="text.secondary">
                        {sendBlocker}
                      </Typography>
                    )}
                  </Stack>
                </>
              )}
              {sendResult && (
                <Alert severity={sendResult.severity} sx={{ mt: 1.5 }} onClose={() => setSendResult(null)}>
                  {sendResult.message}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      )}

      <Dialog open={confirmSend} onClose={() => !sending && setConfirmSend(false)} aria-labelledby="qb-send-title">
        <DialogTitle id="qb-send-title">{`Send ${monthLabel(month)} to QuickBooks?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`This creates one journal entry for ${scopeLabel} in ${qbStatus?.companyName || 'your QuickBooks company'}, dated ${
              range ? range.to : ''
            }. Each month can only be sent once, so check the numbers first.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)} disabled={sending}>
            Not yet
          </Button>
          <Button variant="contained" onClick={send} disabled={sending}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box component="section" aria-labelledby="accounts-heading">
        <Typography id="accounts-heading" variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
          QuickBooks accounts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {pickFromQuickBooks
            ? 'Pick each account from your QuickBooks chart of accounts.'
            : 'Type each account name the way it appears in your QuickBooks chart of accounts.'}{' '}
          You can use the same account for more than one row. Leave GST/HST empty if you don’t claim it back; the tax
          then stays in each expense.
        </Typography>
        {mappingError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {mappingError}
          </Alert>
        )}
        {qbAccountsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {qbAccountsError}
          </Alert>
        )}
        {!mappingError && categories.length > 0 && (
          <>
            {GROUPS.map((group) => {
              const rows = categories.filter((c) => c.group === group.id);
              if (!rows.length) return null;
              return (
                <Box key={group.id} sx={{ mb: 2 }}>
                  <Typography variant="overline" component="h3" color="text.secondary">
                    {group.title}
                  </Typography>
                  <Stack spacing={1.25}>
                    {rows.map((c) => {
                      const value = draft[c.id] || '';
                      const needed = usedThisMonth.has(c.id) && !value.trim();
                      const unknown = unknownAccount(value);
                      const placeholder =
                        c.id === 'expense_tax'
                          ? 'Leave empty to keep tax in each expense'
                          : pickFromQuickBooks
                            ? 'Choose a QuickBooks account'
                            : c.suggestedAccount;
                      const helperText = needed
                        ? `Needed for ${monthLabel(month)}`
                        : unknown
                          ? 'Not in your QuickBooks company. Pick one from the list.'
                          : undefined;
                      const ariaLabel = `QuickBooks account for ${c.label}`;
                      const setValue = (next) => {
                        setSaveResult(null);
                        setDraft((prev) => ({ ...prev, [c.id]: next }));
                      };
                      return (
                        <Stack
                          key={c.id}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 0.5, sm: 2 }}
                          alignItems={{ sm: 'center' }}
                        >
                          <Typography variant="body2" sx={{ flex: { sm: '0 0 300px' } }}>
                            {c.label}
                            {c.craLine && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {` · T776 line ${c.craLine}`}
                              </Typography>
                            )}
                          </Typography>
                          {pickFromQuickBooks ? (
                            <Autocomplete
                              freeSolo
                              forcePopupIcon
                              openOnFocus
                              fullWidth
                              size="small"
                              options={accountOptions}
                              getOptionLabel={(o) => (typeof o === 'string' ? o : o.fullName)}
                              groupBy={(o) => o.type || 'Other'}
                              filterOptions={filterAccountOptions}
                              inputValue={value}
                              onInputChange={(_e, next) => setValue(next)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={placeholder}
                                  error={needed || unknown}
                                  helperText={helperText}
                                  inputProps={{ ...params.inputProps, 'aria-label': ariaLabel, maxLength: 100 }}
                                />
                              )}
                            />
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              value={value}
                              placeholder={placeholder}
                              onChange={(e) => setValue(e.target.value)}
                              error={needed}
                              helperText={helperText}
                              slotProps={{ htmlInput: { 'aria-label': ariaLabel, maxLength: 100 } }}
                            />
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
            {saveResult && (
              <Alert severity={saveResult.severity} sx={{ mb: 2 }} onClose={() => setSaveResult(null)}>
                {saveResult.message}
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4 }}>
              <Button variant="outlined" onClick={fillSuggestions}>
                {pickFromQuickBooks ? 'Fill in matching QuickBooks accounts' : 'Fill empty rows with suggested names'}
              </Button>
              <Button variant="contained" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save accounts'}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}

export default AccountingPreview;
